// src/App.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut } from "firebase/auth";
import { collection, getDocs, doc, getDoc, setDoc, addDoc } from "firebase/firestore";
import { BrowserRouter as Router, Routes, Route, useParams } from "react-router-dom";
import { auth, db } from "./firebase";
import Header from "./components/Header";
import VideoPlayer from "./components/VideoPlayer";
import LoopControls from "./components/LoopControls";
import QuickAddButton from "./components/QuickAddButton";
import TapBPM from "./components/TapBPM";
import FloatingFilePanel from "./components/FloatingFilePanel";
import CollapsibleSection from "./components/CollapsibleSection";
import { spacing, fontSizes, buttons, rounded } from "./styles/tokens";
import CreatableSelect from "react-select/creatable";
import ErrorBoundary from "./components/ErrorBoundary";
import EmailAuthForm from "./components/EmailAuthForm";
import YouTubeSearch from "./components/YouTubeSearch";
import {
  IconMetronome,
  IconSlice,
  IconTrash,
  IconPlayerPlay,
} from "@tabler/icons-react";
import WheelPicker from "./components/WheelPicker";
import GuestWelcomeModal from "./components/GuestWelcomeModal";
import { initEventLogger, logEvent, logError, setSessionId } from "./components/eventLogger";
import LogsViewer from "./components/LogsViewer";


const audioCtx = new (window.AudioContext || window.webkitAudioContext)();

const defaultBookmark = {
  title: "",
  start: 0,
  end: 0,
  speed: 1,
  bpm: 0,
  leadInBeats: 0,
  videoId: "",
};

function SharedLoader({ setSharedImportData, setShowFilePanel }) {
  const { docId } = useParams();

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, "sharedExports", docId));
        if (snap.exists()) {
          setSharedImportData(snap.data().data || []);
          setShowFilePanel(true);
        } else {
          alert("Shared link not found.");
        }
      } catch (err) {
        console.error("Error loading shared data:", err);
        alert("Failed to load shared data.");
      }
    };
    load();
  }, [docId, setSharedImportData, setShowFilePanel]);

  return null;
}

function MainApp({ user, sharedImportData = [] }) {
  const [bookmark, setBookmark] = useState(defaultBookmark);
  const [videoInput, setVideoInput] = useState(""); // manual text/URL typed by the user
  const [selectedVideoId, setSelectedVideoId] = useState("");
  const [videoBookmarks, setVideoBookmarks] = useState([]);
  const [duration, setDuration] = useState(0);
  const [loopEnabled, setLoopEnabled] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [quickAddOffset, setQuickAddOffset] = useState(3);
  const [playbackExpandKey, setPlaybackExpandKey] = useState(0);
  const [countdownOverlay, setCountdownOverlay] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [showFilePanel, setShowFilePanel] = useState(false);
  const [savedVideos, setSavedVideos] = useState({});
  const [selectedBookmarkId, setSelectedBookmarkId] = useState("");
  const [availableBookmarks, setAvailableBookmarks] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoTitle, setVideoTitle] = useState("");
  const [activeTopSection, setActiveTopSection] = useState("Video Load");
  const [countInEnabled, setCountInEnabled] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [showBeatsPicker, setShowBeatsPicker] = useState(false);
  const [showGuestWelcome, setShowGuestWelcome] = useState(false);

  const hasLoadedVideo = !!selectedVideoId;

  // Remount key that clears the Video <CreatableSelect> only when a YT video is loaded
  const [videoSelectResetKey, setVideoSelectResetKey] = useState(0);

  // Track the saved-video selection (so the Load button knows what to load)
  const [selectedSavedVideoId, setSelectedSavedVideoId] = useState("");
  const [selectedSavedVideoTitle, setSelectedSavedVideoTitle] = useState("");

  // Tabs for Video & Bookmark Loading
  const [loadTab, setLoadTab] = useState("search"); // "search" | "existing"

  const dismissGuestWelcome = () => {
    localStorage.setItem("loopy.guestWelcomeSeen", "1");
    setShowGuestWelcome(false);
  };

  useEffect(() => {
    const SEEN_KEY = "loopy.guestWelcomeSeen";
    if (!user) {
      const seen = localStorage.getItem(SEEN_KEY) === "1";
      setShowGuestWelcome(!seen);
    } else {
      // Logged in -> just make sure it’s closed.
      setShowGuestWelcome(false);
      // (Do NOT set the seen flag here.)
    }
  }, [user]);

  const handleEmailLoginPopup = () => {
    logEvent("login_email_popup");
    window.open("/email-login", "Email Login", "width=400,height=600");
  };

  const handleShowVideoLoad = () => {
    setActiveTopSection("Video Load");
  };

  const playerRef = useRef(null);

  const reloadAvailableBookmarks = async (videoId) => {
    if (!user || !videoId) return;
    const snap = await getDocs(collection(db, "users", user.uid, "videos", videoId, "bookmarks"));
    const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setAvailableBookmarks(data);
  };

  const refreshSavedVideos = useCallback(async () => {
    if (!user) return;
    const snap = await getDocs(collection(db, "users", user.uid, "videos"));
    const out = {};
    for (const doc of snap.docs) {
      out[doc.id] = doc.data();
    }
    setSavedVideos(out);
  }, [user]);

  const reloadBookmarks = useCallback(async () => {
    if (!user || !selectedVideoId) return;
    const snap = await getDocs(collection(db, "users", user.uid, "videos", selectedVideoId, "bookmarks"));
    const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setVideoBookmarks(data);
  }, [user, selectedVideoId]);

  useEffect(() => {
    if (sharedImportData.length > 0) {
      setShowFilePanel(true);
    }
  }, [sharedImportData]);

  useEffect(() => {
    if (user) {
      refreshSavedVideos();
    }
  }, [user, refreshSavedVideos]);

  useEffect(() => {
    if (selectedVideoId && savedVideos[selectedVideoId]) {
      reloadBookmarks();
    }
  }, [savedVideos, selectedVideoId, reloadBookmarks]);

  const extractVideoId = (input) => {
    try {
      const url = new URL(input);
      if (url.hostname.includes("youtube.com")) {
        return url.searchParams.get("v");
      } else if (url.hostname === "youtu.be") {
        return url.pathname.slice(1);
      }
    } catch {
      return input;
    }
  };

  const handleLogin = () => {
    logEvent("login_google_click");
    return signInWithPopup(auth, new GoogleAuthProvider());
  };
  const handleLogout = () => {
    logEvent("logout_click");
    return signOut(auth);
  };

  const handleSetBookmark = (bm) => {
    if (bm.videoId !== bookmark.videoId) {
      setVideoBookmarks([]);
    }

    const normalized = {
      ...bm,
      bpm: bm.bpm ?? 0,
      leadInBeats: bm.leadInBeats ?? 0,
      speed: bm.speed ?? 1,
    };

    setSelectedVideoId(normalized.videoId);
    setBookmark(normalized);

    // 👇 also restore the saved enable/disable state (default false if missing)
    setCountInEnabled(bm.countInEnabled ?? false);

    setPlaybackExpandKey((k) => k + 1);

    if (bm && bm.id) {
      logEvent("bookmark_select", { videoId: bm.videoId, sectionId: bm.id });
    }
  };

  const playWithCountdown = useCallback(() => {
    if (!playerRef.current?.seekTo || !playerRef.current?.playVideo) return;

    const bpm = bookmark.bpm || 0;
    const beats = bookmark.leadInBeats || 0;

    if (bpm > 0 && beats > 0) {
      const interval = 60000 / bpm;
      const startTime = audioCtx.currentTime;

      for (let i = 0; i < beats; i++) {
        const time = startTime + i * (interval / 1000);
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(1000, time);
        gain.gain.setValueAtTime(0.1, time);
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.start(time);
        osc.stop(time + 0.1);

        setTimeout(() => {
          const remaining = beats - i;
          setCountdownOverlay({
            text: remaining > 1 ? remaining.toString() : "1",
            style: { fontSize: "20vw" },
          });
        }, i * interval);
      }

      setTimeout(() => {
        setCountdownOverlay(null);
        playerRef.current.seekTo(bookmark.start);
        playerRef.current.playVideo();
      }, beats * interval);
    } else {
      playerRef.current.seekTo(bookmark.start);
      playerRef.current.playVideo();
    }
  }, [bookmark]);

  return (
    <div className={`${spacing.md} space-y-4`}>
      {/* Login modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-md space-y-4 w-80">
            <h2 className="text-lg font-semibold text-center">Login</h2>
            <button
              onClick={() => {
                handleLogin();
                setShowLoginModal(false);
                logEvent("login_google");
              }}
              className={buttons.whitePurple}
            >
              Login with Google
            </button>
            <button
              onClick={() => {
                handleEmailLoginPopup();
                setShowLoginModal(false);
                logEvent("login_email");
              }}
              className={buttons.whitePurple}
            >
              Login with Email
            </button>
            <button
              onClick={() => setShowLoginModal(false)}
              className="text-sm text-gray-500 hover:underline block text-center"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <Header
        user={user}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onShowEmailLogin={() => setShowEmailLogin(true)}
        onShowLoginOptions={() => setShowLoginModal(true)}
        onToggleGuide={() => setShowGuide(!showGuide)}
        onToggleFilePanel={() => { const open = !showFilePanel; setShowFilePanel(open); logEvent("filepanel_toggle", { open }); }}
        onShowVideoLoad={handleShowVideoLoad}
        onShowMetronome={() => setActiveTopSection("Metronome")}
        onShowQuickAdd={() => setActiveTopSection("Quick Add")}
        activeTopSection={activeTopSection}
      />

      {!user && showEmailLogin && (
        <EmailAuthForm
          onClose={() => setShowEmailLogin(false)}
          onSuccess={() => {
            setShowEmailLogin(false);
            window.location.reload();
          }}
        />
      )}

      {showFilePanel && (
        <FloatingFilePanel
          user={user}
          onClose={() => setShowFilePanel(false)}
          reloadCallback={async (videoId) => {
            // 1) Always refresh the list used to build the dropdown options
            logEvent("filepanel_reload", { videoId });
            await refreshSavedVideos();

            // 2) If the affected video matches the one whose bookmarks we’re looking at,
            //    refresh both bookmark lists (available + loaded)
            if (videoId) {
              await reloadAvailableBookmarks(videoId);
              if (selectedVideoId === videoId) {
                await reloadBookmarks();
              }
            }

            // 3) If the deleted/affected video was selected in the dropdown,
            //    clear the selection and force the <CreatableSelect> to remount
            //    so it can’t display a ghost option that no longer exists.
            if (typeof selectedSavedVideoId !== "undefined" && selectedSavedVideoId === videoId) {
              setSelectedSavedVideoId("");
              setSelectedSavedVideoTitle && setSelectedSavedVideoTitle("");
              setVideoSelectResetKey((k) => k + 1);
            }

            // 4) Clear any selected section (avoids stale section ids)
            setSelectedBookmarkId("");
          }}
          sharedImportData={sharedImportData}
        />

      )}

      {activeTopSection === "Metronome" && (
        <CollapsibleSection
          title={
            <div className="flex items-center gap-2">
              <IconMetronome size={18} />
              Metronome Count-in
            </div>
          }
          defaultCollapsed={false}
        >
          <div className="w-full max-w-2xl mx-auto mt-2">
            <div className="mb-3">
              <label className={`flex items-center gap-2 ${fontSizes.sm}`}>
                <input
                  type="checkbox"
                  checked={countInEnabled}
                  onChange={(e) => setCountInEnabled(e.target.checked)}
                />
                Enable Count-in
              </label>
            </div>

            <div className="grid grid-cols-[auto,1fr] items-center gap-x-6 gap-y-4">
              <div>
                <TapBPM bpm={bookmark.bpm} onBPMChange={(bpm) => setBookmark({ ...bookmark, bpm })} />
              </div>

              <div className="relative inline-block">
                <button
                  type="button"
                  className={`${buttons.activePurple} ${rounded.md} px-4 py-2`}
                  onClick={() => setShowBeatsPicker((v) => !v)}
                  aria-haspopup="listbox"
                  aria-expanded={showBeatsPicker}
                >
                  Beats: {bookmark.leadInBeats ?? 0}
                </button>

                {showBeatsPicker && (
                  <WheelPicker
                    options={[0, 1, 2, 3, 4, 5, 6, 7, 8]}                 // include 0
                    value={bookmark.leadInBeats ?? 0}                      // show 0 instead of falling back to 4
                    onChange={(n) => setBookmark((b) => ({ ...b, leadInBeats: n }))}
                    onClose={() => setShowBeatsPicker(false)}
                  />
                )}
              </div>
            </div>
          </div>
        </CollapsibleSection>
      )}

      {user && activeTopSection === "Quick Add" && (
        <CollapsibleSection
          title={
            <div className="flex items-center gap-2">
              <IconSlice size={18} />
              Slice Video
            </div>
          }
          defaultCollapsed={false}
        >
          <div className="grid grid-cols-[auto,1fr] items-center gap-x-6 gap-y-4 mt-4 w/full max-w-2xl mx-auto">
            <button
              className={buttons.activePurpleSm}
              onClick={() => {
                if (!playerRef.current?.getCurrentTime) return;
                const time = playerRef.current.getCurrentTime();
                setMarkers((prev) => [...prev, parseFloat(time.toFixed(2))].sort((a, b) => a - b));
                logEvent("marker_add", { time });
              }}
            >
              Add Divider
            </button>

            <div className="flex items-center gap-4">
              <button
                className={`${buttons.whitePurple} ${buttons.purpleHover} ${rounded.md}`}
                onClick={async () => {
                  if (!user || !selectedVideoId || markers.length === 0 || !duration) {
                    alert("Add at least one divider first.");
                    return;
                  }

                  const points = [0, ...markers, duration];
                  const sectionsSaved = points.length - 1;

                  logEvent("video_slice", { videoId: selectedVideoId, sections: sectionsSaved, markers });

                  const videoDocRef = doc(db, "users", user.uid, "videos", selectedVideoId);
                  await setDoc(videoDocRef, { title: videoTitle || "Untitled Video" }, { merge: true });

                  const bookmarksRef = collection(videoDocRef, "bookmarks");
                  const snapshot = await getDocs(bookmarksRef);

                  const usedNumbers = snapshot.docs
                    .map((doc) => doc.data().title || "")
                    .filter((title) => title.startsWith("Section "))
                    .map((title) => parseInt(title.replace("Section ", "")))
                    .filter((n) => !isNaN(n));

                  const maxUsed = usedNumbers.length > 0 ? Math.max(...usedNumbers) : 0;
                  let sectionIndex = maxUsed + 1;

                  for (let i = 0; i < points.length - 1; i++) {
                    const start = points[i];
                    const end = points[i + 1];
                    const newDoc = await addDoc(bookmarksRef, {
                      title: `Section ${sectionIndex++}`,
                      videoId: selectedVideoId,
                      videoTitle,
                      start: parseFloat(start.toFixed(2)),
                      end: parseFloat(end.toFixed(2)),
                      speed: 1.0,
                      uid: user.uid,
                      bpm: bookmark.bpm ?? 0,
                      leadInBeats: bookmark.leadInBeats ?? 0,
                      countInEnabled, // boolean from state
                    });
                    await setDoc(newDoc, { id: newDoc.id }, { merge: true });
                  }

                  setMarkers([]);
                  await reloadBookmarks();
                  refreshSavedVideos();

                  setCountdownOverlay({
                    text: `✅ ${sectionsSaved} Section${sectionsSaved !== 1 ? "s" : ""} Saved`,
                    style: { fontSize: "6vw" },
                  });
                  setTimeout(() => setCountdownOverlay(null), 1200);
                }}
              >
                <div className="flex items-center gap-2">
                  <IconSlice size={18} />
                  Slice Video
                </div>
              </button>

              {markers.length > 0 && (
                <button
                  className={`${buttons.whitePurple} ${buttons.purpleHover} ${rounded.md}`}
                  onClick={() => { setMarkers([]); logEvent("markers_clear"); }}
                  title="Remove all dividers"
                >
                  <div className="flex items-center gap-2">
                    <IconTrash size={18} />
                    Clear Dividers
                  </div>
                </button>
              )}
            </div>

            <div>
              <QuickAddButton
                user={user}
                videoId={bookmark.videoId}
                videoTitle={videoTitle}
                playerRef={playerRef}
                reloadCallback={reloadBookmarks}
                offset={quickAddOffset}
                onSuccess={() => {
                  setCountdownOverlay({ text: "Bookmark added!", style: { fontSize: "6vw" } });
                  setTimeout(() => setCountdownOverlay(null), 1200);
                  refreshSavedVideos();
                  logEvent("section_quick_add", { videoId: bookmark.videoId, offset: quickAddOffset });
                }}
              />
            </div>

            <div className="flex items-center gap-3">
              <label htmlFor="offset" className={`${fontSizes.sm} whitespace-nowrap`}>
                Length:
              </label>

              <button
                onClick={() => setQuickAddOffset(Math.max(1, quickAddOffset - 1))}
                className={`${fontSizes.sm} px-2 py-1 bg-white border ${rounded.md} hover:bg-gray-100 h-10`}
              >
                −
              </button>

              <input
                id="offset"
                type="number"
                min="1"
                value={quickAddOffset}
                onChange={(e) => setQuickAddOffset(parseInt(e.target.value))}
                className={`w-12 ${fontSizes.sm} text-center p-1 border ${rounded.md} shadow-inner h-10`}
              />

              <button
                onClick={() => setQuickAddOffset(quickAddOffset + 1)}
                className={`${fontSizes.sm} px-2 py-1 bg-white border ${rounded.md} hover:bg-gray-100 h-10`}
              >
                +
              </button>
            </div>
          </div>
        </CollapsibleSection>
      )}

      {activeTopSection === "Video Load" && (
        <CollapsibleSection
          tabs={[
            { id: "search", label: "Search" },
            { id: "existing", label: "Load & Save" },
          ]}
          activeTab={loadTab}
          onTabChange={setLoadTab}
          defaultCollapsed={false}
        >
          {loadTab === "search" && (
            <div className="space-y-3">
              <YouTubeSearch
                onSelect={({ id, title }) => {
                  // Load selected YT video
                  setSelectedVideoId(id);
                  setBookmark((prev) => ({
                    ...defaultBookmark,
                    videoId: id,
                    title: prev.title || "",
                  }));

                  // Store title from YouTube
                  setVideoTitle(title || "");

                  // ✅ Clear the Video select UI ONLY on YT load
                  setVideoInput("");
                  setSelectedSavedVideoId("");
                  setSelectedSavedVideoTitle("");
                  setSelectedBookmarkId("");
                  setAvailableBookmarks([]);
                  setVideoSelectResetKey((k) => k + 1);

                  // Fetch any existing bookmarks for this id
                  reloadAvailableBookmarks(id);

                  setPlaybackExpandKey((k) => k + 1);
                  setLoadTab("existing");

                  logEvent("video_load", { source: "search", videoId: id, title });
                }}
                regionCode="GB"
                safeSearch="moderate"
              />
            </div>
          )}

          {loadTab === "existing" && (
            <form
              onSubmit={(e) => {
                e.preventDefault();

                // Prefer an explicit saved-video pick; else fall back to what's typed
                let matchedId = "";
                let matchedTitle = "";

                if (selectedSavedVideoId) {
                  matchedId = selectedSavedVideoId;
                  matchedTitle = savedVideos[selectedSavedVideoId]?.title || selectedSavedVideoTitle || "";
                } else {
                  matchedId =
                    Object.entries(savedVideos).find(
                      ([, video]) =>
                        (video.title || "").trim().toLowerCase() === videoInput.trim().toLowerCase()
                    )?.[0] || extractVideoId(videoInput);
                  matchedTitle = savedVideos[matchedId]?.title || "";
                }

                const selectedBookmark = availableBookmarks.find((b) => b.id === selectedBookmarkId);

                if (selectedBookmark) {
                  // Load full bookmark
                  logEvent("video_load", { source: "bookmark", videoId: selectedBookmark.videoId, sectionId: selectedBookmark.id });
                  handleSetBookmark(selectedBookmark);
                  setSelectedVideoId(selectedBookmark.videoId);
                  setVideoTitle(matchedTitle);
                } else {
                  // Load video only
                  logEvent("video_load", { source: "url", videoId: matchedId });
                  setSelectedVideoId(matchedId);
                  setBookmark((prev) => ({
                    ...defaultBookmark,
                    videoId: matchedId,
                    title: prev.title || "",
                  }));
                  setVideoTitle(matchedTitle);
                }

                setPlaybackExpandKey((k) => k + 1);
              }}
              className="space-y-2"
            >
              {/* Video Input using react-select/creatable (uncontrolled; selection stays visible) */}
              <CreatableSelect
                key={videoSelectResetKey}
                className="w-full"
                placeholder="Paste YouTube URL or select from list"
                options={Object.entries(savedVideos).map(([id, video]) => ({
                  value: id,
                  label: video.title,
                }))}
                onChange={(opt) => {
                  if (!opt) return;
                  // Remember selection for Load (but keep UI selected)
                  setSelectedSavedVideoId(opt.value);
                  setSelectedSavedVideoTitle(opt.label || "");

                  // Update bookmarks for that video id
                  reloadAvailableBookmarks(opt.value);
                  setSelectedBookmarkId("");
                }}
                onCreateOption={(inputValue) => {
                  // User typed a URL/title and pressed Enter; keep text in state for Load fallback
                  setVideoInput(inputValue);
                  const extracted = extractVideoId(inputValue);
                  if (extracted) reloadAvailableBookmarks(extracted);
                  setSelectedSavedVideoId(""); // not a saved-video pick
                  setSelectedSavedVideoTitle("");
                  setSelectedBookmarkId("");
                }}
                onInputChange={(val, { action }) => {
                  if (action === "input-change") {
                    setVideoInput(val);
                    setSelectedSavedVideoId("");   // typing cancels explicit pick
                    setSelectedSavedVideoTitle("");
                    setSelectedBookmarkId("");
                  }
                }}
                isClearable
                isSearchable
                formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
                backspaceRemovesValue
                /* layering fix */
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />

              {/* Bookmark Select */}
              <CreatableSelect
                className="w-full"
                placeholder="Enter or select a section title"
                options={availableBookmarks.map((bm) => ({
                  value: bm.id,
                  label: bm.title,
                }))}
                value={
                  selectedBookmarkId
                    ? { value: selectedBookmarkId, label: bookmark.title }
                    : bookmark.title
                    ? { value: null, label: bookmark.title }
                    : null
                }
                onChange={(selectedOption) => {
                  if (!selectedOption) {
                    setSelectedBookmarkId("");
                    setBookmark((prev) => ({ ...prev, title: "", id: undefined }));
                    return;
                  }
                  if (selectedOption.__isNew__) {
                    setBookmark((prev) => ({ ...prev, title: selectedOption.label }));
                    return;
                  }
                  setSelectedBookmarkId(selectedOption.value);
                  setBookmark((prev) => ({
                    ...prev,
                    title: selectedOption.label,
                    id: selectedOption.value,
                  }));
                }}
                isClearable
                isSearchable
                backspaceRemovesValue
                formatCreateLabel={(inputValue) => `Create new: "${inputValue}"`}
                /* layering fix */
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />

              {/* Load & Save Buttons */}
              <div className="flex justify-center flex-wrap gap-2">
                <button
                  type="submit"
                  className={
                    hasLoadedVideo
                      ? `${buttons.whitePurple} ${buttons.purpleHover} ${rounded.md}`   // de-emphasized once loaded
                      : `${buttons.activePurple} px-4 py-2 ${rounded.md}`                // emphasized before anything is loaded
                  }
                >
                  Load
                </button>

                {user && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!bookmark.videoId || !bookmark.title) {
                        alert("Please enter a section title and load a video first.");
                        return;
                      }

                      const videoRef = doc(db, "users", user.uid, "videos", bookmark.videoId);
                      await setDoc(videoRef, { title: videoTitle || "Untitled Video" }, { merge: true });

                      const sectionCol = collection(videoRef, "bookmarks");

                      const normalize = (s) => (s || "").trim();
                      const titlesEqual = (a, b) => normalize(a).toLowerCase() === normalize(b).toLowerCase();
                      const approx = (a, b, eps = 0.01) => Math.abs(Number(a ?? 0) - Number(b ?? 0)) < eps;

                      const findById = (id) =>
                        (videoBookmarks?.find((b) => b.id === id) ||
                          availableBookmarks?.find((b) => b.id === id)) || null;

                      const hasLoadedId = !!bookmark.id;
                      const existing = hasLoadedId ? findById(bookmark.id) : null;

                      if (hasLoadedId) {
                        const loaded = existing;
                        const nameChanged = loaded ? !titlesEqual(loaded.title, bookmark.title) : false;
                        const keysChanged = loaded
                          ? !approx(loaded.start, bookmark.start) ||
                            !approx(loaded.end, bookmark.end) ||
                            !approx(loaded.speed ?? 1, bookmark.speed ?? 1)
                          : false;

                        if (nameChanged && keysChanged) {
                          const { id, ...safeBookmark } = bookmark;
                          const newDoc = await addDoc(sectionCol, {
                            ...safeBookmark,
                            videoTitle,
                            uid: user.uid,
                            bpm: bookmark.bpm ?? 0,
                            leadInBeats: bookmark.leadInBeats ?? 0,
                            countInEnabled,
                          });
                          await setDoc(newDoc, { id: newDoc.id }, { merge: true });
                          alert("New section created.");
                          logEvent("section_save", { action: "create_new", ...safeBookmark, sectionId: newDoc.id });
                        } else {
                          const sectionDoc = doc(sectionCol, bookmark.id);
                          const update = {
                            videoId: bookmark.videoId,
                            videoTitle,
                            uid: user.uid,
                            title: bookmark.title,
                            start: bookmark.start,
                            end: bookmark.end,
                            speed: bookmark.speed ?? 1,
                            bpm: bookmark.bpm ?? 0,
                            leadInBeats: bookmark.leadInBeats ?? 0,
                            countInEnabled, // from state
                          };
                          await setDoc(sectionDoc, update, { merge: true });
                          alert("Section updated.");
                          logEvent("section_save", { action: "update", sectionId: bookmark.id, ...update });
                        }
                      } else {
                        const matchByTitle = availableBookmarks.find((b) => titlesEqual(b.title, bookmark.title));
                        if (matchByTitle) {
                          const sectionDoc = doc(sectionCol, matchByTitle.id);
                          await setDoc(
                            sectionDoc,
                            {
                              title: bookmark.title,
                              start: bookmark.start,
                              end: bookmark.end,
                              speed: bookmark.speed ?? 1,
                              videoId: bookmark.videoId,
                              videoTitle,
                              uid: user.uid,
                              bpm: bookmark.bpm ?? 0,
                              leadInBeats: bookmark.leadInBeats ?? 0,
                              countInEnabled,
                            },
                            { merge: true }
                          );
                          alert("Section updated.");
                          logEvent("section_save", { action: "update_by_title", sectionId: matchByTitle.id, videoId: bookmark.videoId, title: bookmark.title });
                        } else {
                          const { id, ...safeBookmark } = bookmark;
                          const newDoc = await addDoc(sectionCol, {
                            ...safeBookmark,
                            videoTitle,
                            uid: user.uid,
                            bpm: bookmark.bpm ?? 0,
                            leadInBeats: bookmark.leadInBeats ?? 0,
                            countInEnabled,
                          });
                          await setDoc(newDoc, { id: newDoc.id }, { merge: true });
                          alert("Section saved.");
                          logEvent("section_save", { action: "create", ...safeBookmark, sectionId: newDoc.id });
                        }
                      }

                      await reloadAvailableBookmarks(bookmark.videoId);
                      await reloadBookmarks();
                      refreshSavedVideos();
                      setPlaybackExpandKey((k) => k + 1);
                    }}
                    className={
                      hasLoadedVideo
                        ? `${buttons.activePurple} px-4 py-2 ${rounded.md}`              // emphasized once something is loaded
                        : `${buttons.whitePurple} ${buttons.purpleHover} ${rounded.md}`  // de-emphasized initially
                    }
                  >
                    Save
                  </button>
                )}
              </div>
            </form>
          )}
        </CollapsibleSection>
      )}

      {selectedVideoId && (
        <ErrorBoundary>
          <VideoPlayer
            videoId={selectedVideoId}
            playerRef={playerRef}
            onDuration={(d) => {
              setDuration(d);
              if (!selectedBookmarkId) {
                setBookmark({
                  ...defaultBookmark,
                  videoId: selectedVideoId,
                  end: d,
                });
              }
            }}
            onLoaded={async () => {
              const player = playerRef.current;
              if (!player?.getVideoData) return;
              let attempts = 0;
              const maxAttempts = 10;
              let videoTitleFromPlayer = "";
              while (attempts < maxAttempts) {
                const title = player.getVideoData().title;
                if (title) {
                  videoTitleFromPlayer = title;
                  break;
                }
                await new Promise((res) => setTimeout(res, 200));
                attempts++;
              }
              if (videoTitleFromPlayer) {
                setVideoTitle(videoTitleFromPlayer);
                setBookmark((prev) => ({ ...prev, videoTitle: videoTitleFromPlayer }));
              } else {
                console.warn("⚠️ Failed to extract title after retries.");
              }
              if (savedVideos[selectedVideoId]) {
                await reloadBookmarks();
              } else {
                setVideoBookmarks([]);
              }
            }}
            loopEnabled={loopEnabled}
            countInEnabled={countInEnabled}
            bookmark={bookmark}
            countdownOverlay={countdownOverlay}
            setCurrentTime={setCurrentTime}
            setIsPlaying={setIsPlaying}
            onLoopRestart={playWithCountdown}
          />
        </ErrorBoundary>
      )}

      <CollapsibleSection
        title={
          <div className="flex items-center gap-2">
            <IconPlayerPlay size={18} />
            Playback Controls
          </div>
        }
        defaultCollapsed={true}
        forceExpandSignal={playbackExpandKey}
      >
        <ErrorBoundary>
          <LoopControls
            loopSettings={bookmark}
            onChange={setBookmark}
            duration={duration}
            playerRef={playerRef}
            bookmarks={videoBookmarks}
            onBookmarkSelect={(bm) => {
              if (bm === "count-in") {
                if (countInEnabled && bookmark.bpm > 0 && bookmark.leadInBeats > 0) {
                  playWithCountdown();
                } else {
                  playerRef.current?.seekTo?.(bookmark.start);
                  playerRef.current?.playVideo?.();
                  setIsPlaying(true);
                }
              } else {
                handleSetBookmark(bm);
                if (bm && bm.id) { logEvent("bookmark_select", { videoId: bm.videoId, sectionId: bm.id }); }
              }
            }}
            currentTime={currentTime}
            isPlaying={isPlaying}
            setIsPlaying={setIsPlaying}
            loopEnabled={loopEnabled}
            setLoopEnabled={setLoopEnabled}
            markers={markers}
            setMarkers={setMarkers}
            setCurrentTime={setCurrentTime}
          />
        </ErrorBoundary>
      </CollapsibleSection>
      <GuestWelcomeModal
        open={showGuestWelcome && !user}
        onClose={dismissGuestWelcome}
      />
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [sharedImportData, setSharedImportData] = useState([]);
  const loggerInitRef = useRef(false);

  useEffect(() => {
    onAuthStateChanged(auth, (u) => {
      if (u && !u.emailVerified && u.providerData[0]?.providerId === "password") {
        signOut(auth);
        setUser(null);
      } else {
        setUser(u);
      }
      if (!loggerInitRef.current) {
        initEventLogger({ db, getUser: () => auth.currentUser, appVersion: "web-1.0.0" });
        try { setSessionId(crypto.randomUUID()); } catch {}
        loggerInitRef.current = true;
      }
    });
  }, []);

  // Optional: global error capture
  useEffect(() => {
    const onErr = (e) => logError(e.error || e, { source: "window_error" });
    const onRej = (e) => logError(e.reason || e, { source: "unhandled_rejection" });
    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onRej);
    return () => {
      window.removeEventListener("error", onErr);
      window.removeEventListener("unhandledrejection", onRej);
    };
  }, []);

  const setShowFilePanel = () => {}; // dummy to satisfy SharedLoader early

  return (
    <Router>
      <Routes>
        <Route path="/email-login" element={<EmailAuthForm onSuccess={() => window.close()} />} />
        <Route
          path="/shared/:docId"
          element={
            <>
              <SharedLoader setSharedImportData={setSharedImportData} setShowFilePanel={setShowFilePanel} />
              <MainApp user={user} sharedImportData={sharedImportData} />
            </>
          }
        />
        <Route path="/" element={<MainApp user={user} />} />
        <Route path="/logs" element={<LogsViewer />} />
      </Routes>
    </Router>
  );
}

export default App;
